import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbInputDatepicker, NgbTimeStruct
} from "@ng-bootstrap/ng-bootstrap";
import {DateHelpers} from "../../helpers/date-helpers";
import {DateTime, Duration} from "luxon";
import {auto} from "@popperjs/core";

@Component({
  selector: 'ngbd-datepicker-range',
  templateUrl: './ngbd-datepicker-range.component.html',
  styleUrls: ['./ngbd-datepicker-range.component.scss']
})
export class NgbdDatepickerRangeComponent implements OnInit {
  hoveredDate: NgbDate;

  @Input() fromDate?: Date;
  @Input() toDate?: Date;
  @Input() showTime: boolean = false;
  @Input() showSeconds: boolean = false;
  @Input() initOpened = false;
  @Input() class: string;
  @Input() autoClose: boolean | 'inside' | 'outside' = 'outside';
  @Output() dateSelect = new EventEmitter<{ From: Date, To: Date }>();

  fromNgbDate: NgbDate = null;
  toNgbDate: NgbDate = null;
  date: any;
  fromTime = {hour: 0, minute: 0, second: 0} as NgbTimeStruct;
  toTime = {hour: 0, minute: 0, second: 0} as NgbTimeStruct;
  showTimePicker: boolean = false;

  @ViewChild(NgbInputDatepicker, {static: true}) picker: NgbInputDatepicker;
  private selectNeeded: boolean = false;

  constructor(private calendar: NgbCalendar, public formatter: NgbDateParserFormatter) {

  }

  ngOnInit(): void {
    this.initializeSelection();
  }

  ngAfterViewInit(): void {
    this.picker?.closed.subscribe($event => {
      this.showTimePicker = false;
    });
    if (this.initOpened) {
      this.picker.open();
    }
  }

  ngOnChanges(changes: SimpleChanges): void{
    if (changes.fromDate && !changes.fromDate.isFirstChange() || changes.toDate && !changes.toDate.isFirstChange()) {
      this.initializeSelection();
    }
  }

  private initializeSelection() {
    if (this.fromDate) {
      this.fromNgbDate = new NgbDate(this.fromDate.getFullYear(), this.fromDate.getMonth() + 1, this.fromDate.getDate());
    }
    if (this.toDate) {
      this.toNgbDate = new NgbDate(this.toDate.getFullYear(), this.toDate.getMonth() + 1, this.toDate.getDate());
    }
  }

  toggle() {
    return this.picker?.toggle();
  }

  onDateSelection(date: NgbDate) {
    if (this.fromNgbDate && this.toNgbDate && (this.fromNgbDate.equals(date) || this.toNgbDate.equals(date))) {
      if (this.fromNgbDate.equals(date)) {
        this.toNgbDate = null;
      } else if (this.toNgbDate.equals(date)) {
        this.fromNgbDate = null;
      }
    } else if (this.fromNgbDate && this.fromNgbDate.equals(date)) {
      if (!this.toNgbDate) {
        this.toNgbDate = date;
      } else {
        this.fromNgbDate = null;
      }
    } else if (this.toNgbDate && this.toNgbDate.equals(date)) {
      this.toNgbDate = null;
    } else {
      if (!this.fromNgbDate && !this.toNgbDate) {
        this.fromNgbDate = date;
      } else if (this.fromNgbDate && !this.toNgbDate && date.after(this.fromNgbDate)) {
        this.toNgbDate = date;
      } else {
        this.toNgbDate = null;
        this.fromNgbDate = date;
      }
    }

    if (this.fromNgbDate) {
      this.setFromDate();
    } else {
      this.fromDate = null;
    }

    if (this.toNgbDate !== null) {
      this.setToDate();
    } else {
      this.toDate = null;
    }

    this.updateTime();
    this.emitSelection();
  }

  private setFromDate() {
    this.fromDate = new Date(this.fromNgbDate.year, this.fromNgbDate.month - 1, this.fromNgbDate.day);
  }

  private setToDate() {
    this.toDate = new Date(this.toNgbDate.year, this.toNgbDate.month - 1, this.toNgbDate.day, 23, 59, 59);
  }

  isHovered(date: NgbDate) {
    return this.fromNgbDate && !this.toNgbDate && this.hoveredDate && date.after(this.fromNgbDate) && date.before(this.hoveredDate);
  }

  isInside(date: NgbDate) {
    return date.after(this.fromNgbDate) && date.before(this.toNgbDate);
  }

  isRange(date: NgbDate) {
    return date.equals(this.fromNgbDate) || date.equals(this.toNgbDate) || this.isInside(date) || this.isHovered(date);
  }

  isFrom(date: NgbDate) {
    return this.fromNgbDate === date;
  }

  isTo(date: NgbDate) {
    return this.toNgbDate === date;
  }

  selectToday() {
    this.select(DateTime.now().startOf('day').toJSDate(), DateTime.now().endOf('day').toJSDate());
  }

  selectYesterday() {
    const dateTime = DateTime.now().minus(Duration.fromObject({days: 1}));
    this.select(dateTime.startOf('day').toJSDate(), dateTime.endOf('day').toJSDate());
  }

  selectThisWeek() {
    this.select(DateTime.now().startOf('week').toJSDate(), DateTime.now().endOf('week').toJSDate());
  }

  selectLastWeek() {
    const dateTime = DateTime.now().minus(Duration.fromObject({weeks: 1}));
    this.select(dateTime.startOf('week').toJSDate(), dateTime.endOf('week').toJSDate());
  }

  selectThisMonth() {
    this.select(DateTime.now().startOf('month').toJSDate(), DateTime.now().endOf('month').toJSDate());
  }

  selectLastMonth() {
    const dateTime = DateTime.now().minus(Duration.fromObject({months: 1}));
    this.select(dateTime.startOf('month').toJSDate(), dateTime.endOf('month').toJSDate());
  }

  selectFromLastMonth() {
    this.select(DateTime.now().minus(Duration.fromObject({months: 1})).startOf('month').toJSDate(), DateTime.now().endOf('day').toJSDate());
  }

  selectFromStartOfYear() {
    this.select(DateTime.now().startOf('year').toJSDate(), DateTime.now().endOf('day').toJSDate());
  }

  selectPreviousPeriod() {
    if (this.fromDate && this.toDate) {
      const fromDateLuxon = DateTime.fromJSDate(this.fromDate).startOf('day');
      const toDateLuxon = DateTime.fromJSDate(this.toDate).endOf('day');
      const previousToDate = fromDateLuxon.minus(Duration.fromObject({days: 1})).toJSDate();
      const previousFromDate = DateHelpers.isOneMonth(fromDateLuxon, toDateLuxon)
        ? fromDateLuxon.minus(Duration.fromObject({days: 1})).startOf('month').toJSDate()
        : fromDateLuxon.minus(toDateLuxon.diff(fromDateLuxon)).toJSDate();
      this.setPeriod(previousFromDate, previousToDate);
    }
  }

  selectNextPeriod() {
    if (this.fromDate && this.toDate) {
      const fromDateLuxon = DateTime.fromJSDate(this.fromDate).startOf('day');
      const toDateLuxon = DateTime.fromJSDate(this.toDate).endOf('day');
      const nextFromDate = toDateLuxon.plus(Duration.fromObject({days: 1})).toJSDate();
      const nextToDate = DateHelpers.isOneMonth(fromDateLuxon, toDateLuxon)
        ? toDateLuxon.plus(Duration.fromObject({days: 1})).endOf('month').toJSDate()
        : toDateLuxon.plus(toDateLuxon.diff(fromDateLuxon)).toJSDate();
      this.setPeriod(nextFromDate, nextToDate);
    }
  }

  private setPeriod(from: Date, to: Date) {
    this.fromNgbDate = DateHelpers.getNgbDate(from);
    this.toNgbDate = DateHelpers.getNgbDate(to);
    this.setFromDate();
    this.setToDate();
    this.emitSelection();
  }

  select(from: Date, to: Date) {
    this.fromDate = from;
    this.toDate = to;
    this.emitSelection();
    this.ngOnInit();
  }

  validateFromInput(input: string) {
    const parsed = this.formatter.parse(input);
    const ngbDate = NgbDate.from(parsed);
    if (parsed && this.calendar.isValid(ngbDate) && (ngbDate.before(this.toNgbDate) || ngbDate.equals(this.toNgbDate))) {
      this.fromNgbDate = ngbDate
      this.setFromDate();
      this.selectNeeded = true;
    }
  }

  validateToInput(input: string) {
    const parsed = this.formatter.parse(input);
    const ngbDate = NgbDate.from(parsed);
    if (parsed && this.calendar.isValid(ngbDate) && (ngbDate.after(this.fromNgbDate) || ngbDate.equals(this.fromNgbDate))) {
      this.toNgbDate = ngbDate;
      this.setToDate();
      this.selectNeeded = true;
    }
  }

  selectIfNeeded(){
    if (this.selectNeeded) {
      this.emitSelection();
      this.selectNeeded = false;
    }
  }

  emitSelection() {
    this.dateSelect.emit({From: this.fromDate, To: this.toDate});
  }

  toggleDateTimeState($event) {
    this.showTimePicker = !this.showTimePicker;
    this.updateTime();
    $event.stopPropagation();
  }

  private updateTime() {
    this.fromTime = DateHelpers.getNgbTimeStruct(this.fromDate);
    this.toTime = DateHelpers.getNgbTimeStruct(this.toDate);
  }

  onTimeChange(event: NgbTimeStruct, date: Date): Date {
    date ??= new Date();
    date.setHours(event.hour, event.minute, event.second);
    return new Date(date);
  }
}
